import {
  collection,
  getDocs,
  query,
  where,
  type QueryDocumentSnapshot,
} from "firebase/firestore";
import { db } from "./firebase";
import type { Institution } from "@/types";

/**
 * The numbers on the overview.
 *
 * **Aggregated from `statistics/`, `institutions/` and `aiUsage/`, and from
 * nothing else.** Never from `sessions/`, never from `events/`, never from a
 * run, a score or a note. Spontar has no business reading an institution's
 * operational data, and the statistics collection exists precisely so that a
 * number can be produced without a query that could have returned a name.
 *
 * A structural test asserts that boundary; the comment is here so somebody
 * about to add a field knows why the test will stop them.
 */

export interface SessionStatisticsRow {
  institutionId: string;
  groupId: string;
  teacherUid: string;
  date: string;
  runCount: number;
  scenarioIds: string[];
  attendanceCount: number;
  rosterSize: number;
  durationMinutes: number;
  mode: "online" | "offline";
}

export interface AiUsageRow {
  institutionId: string;
  feature: string;
  costEur: number;
  at: string;
  outcome: string;
}

export interface Metrics {
  institutions: {
    total: number;
    active: number;
    suspended: number;
    lapsed: number;
    byCountry: Array<{ country: string; count: number }>;
  };
  seats: {
    sold: number;
    used: number;
    byTier: Array<{ tier: string; sold: number; institutions: number }>;
  };
  sessions: {
    week: number;
    month: number;
    online: number;
    offline: number;
  };
  /**
   * The real engagement signal.
   *
   * A lesson with one scene in it is somebody trying the product; four is
   * somebody teaching with it. Sessions counted alone cannot tell those apart,
   * which is why a rising session count can accompany a dying product.
   */
  runsPerSession: {
    average: number;
    distribution: Array<{ runs: number; sessions: number }>;
  };
  ai: {
    monthEur: number;
    previousMonthEur: number;
    byFeature: Array<{ feature: string; eur: number }>;
    refused: number;
  };
}

function monthKey(iso: string): string {
  return iso.slice(0, 7);
}

function countBy<T>(rows: readonly T[], key: (row: T) => string): Map<string, number> {
  const counts = new Map<string, number>();
  for (const row of rows) counts.set(key(row), (counts.get(key(row)) ?? 0) + 1);
  return counts;
}

/**
 * Pure, so the arithmetic can be read and tested without a database.
 *
 * `now` is a parameter for the same reason: a metric that changes depending on
 * when the test runs is a metric nobody trusts.
 */
export function computeMetrics(
  institutions: readonly Institution[],
  statistics: readonly SessionStatisticsRow[],
  aiUsage: readonly AiUsageRow[],
  now: number
): Metrics {
  const weekAgo = new Date(now - 7 * 86_400_000).toISOString();
  const monthAgo = new Date(now - 30 * 86_400_000).toISOString();
  const thisMonth = monthKey(new Date(now).toISOString());
  const lastMonth = monthKey(new Date(now - 30 * 86_400_000).toISOString());

  const lapsed = institutions.filter(
    (institution) => Date.parse(institution.validUntil) < now
  );
  const suspended = institutions.filter((institution) => institution.suspendedAt !== undefined);

  const byTier = new Map<string, { sold: number; institutions: number }>();
  for (const institution of institutions) {
    const entry = byTier.get(institution.licenceTier) ?? { sold: 0, institutions: 0 };
    entry.sold += institution.teacherSeats;
    entry.institutions += 1;
    byTier.set(institution.licenceTier, entry);
  }

  const week = statistics.filter((row) => row.date >= weekAgo);
  const month = statistics.filter((row) => row.date >= monthAgo);

  const runs = countBy(statistics, (row) => String(row.runCount));
  const totalRuns = statistics.reduce((sum, row) => sum + row.runCount, 0);

  const aiThisMonth = aiUsage.filter((row) => monthKey(row.at) === thisMonth);
  const aiLastMonth = aiUsage.filter((row) => monthKey(row.at) === lastMonth);
  const byFeature = new Map<string, number>();
  for (const row of aiThisMonth) {
    byFeature.set(row.feature, (byFeature.get(row.feature) ?? 0) + row.costEur);
  }

  return {
    institutions: {
      total: institutions.length,
      // Suspended and lapsed are different things and are counted apart: one is
      // a decision somebody made, the other is a date passing.
      active: institutions.length - suspended.length - lapsed.length,
      suspended: suspended.length,
      lapsed: lapsed.length,
      byCountry: [...countBy(institutions, (institution) => institution.country)]
        .map(([country, count]) => ({ country, count }))
        .sort((a, b) => b.count - a.count || a.country.localeCompare(b.country)),
    },
    seats: {
      sold: institutions.reduce((sum, institution) => sum + institution.teacherSeats, 0),
      // Seats in use are counted per institution by the backend; the console
      // shows what was sold, which is the number it can know from here.
      used: 0,
      byTier: [...byTier]
        .map(([tier, entry]) => ({ tier, ...entry }))
        .sort((a, b) => b.sold - a.sold),
    },
    sessions: {
      week: week.length,
      month: month.length,
      online: month.filter((row) => row.mode === "online").length,
      offline: month.filter((row) => row.mode === "offline").length,
    },
    runsPerSession: {
      average: statistics.length === 0 ? 0 : totalRuns / statistics.length,
      distribution: [...runs]
        .map(([count, sessions]) => ({ runs: Number(count), sessions }))
        .sort((a, b) => a.runs - b.runs),
    },
    ai: {
      monthEur: aiThisMonth.reduce((sum, row) => sum + row.costEur, 0),
      previousMonthEur: aiLastMonth.reduce((sum, row) => sum + row.costEur, 0),
      byFeature: [...byFeature]
        .map(([feature, eur]) => ({ feature, eur }))
        .sort((a, b) => b.eur - a.eur),
      // A refusal is the AI boundary doing its job, and a rise in refusals is
      // something somebody has to look at rather than a statistic.
      refused: aiUsage.filter((row) => row.outcome === "refused").length,
    },
  };
}

const data = <T,>(snapshot: QueryDocumentSnapshot) => snapshot.data() as T;

/** Reads the three collections the metrics are made of, and nothing else. */
export async function loadMetrics(now: number): Promise<Metrics> {
  const since = new Date(now - 400 * 86_400_000).toISOString();

  const [institutions, statistics, aiUsage] = await Promise.all([
    getDocs(collection(db(), "institutions")),
    getDocs(query(collection(db(), "statistics"), where("date", ">=", since))),
    getDocs(query(collection(db(), "aiUsage"), where("at", ">=", since))),
  ]);

  return computeMetrics(
    institutions.docs.map(data<Institution>),
    statistics.docs.map(data<SessionStatisticsRow>),
    aiUsage.docs.map(data<AiUsageRow>),
    now
  );
}
